/** @module Camunda/TaskActionGroup */
define([
	"dojo/_base/declare",
	"dijit/_TemplatedMixin",
	"dijit/form/RadioButton",
	"dijit/TitlePane",
	"dijit/_WidgetsInTemplateMixin",
	"dijit/_WidgetBase",
	"underscore",
	"csi",
	"csi/_base/Deferred",
	"csi/html",
	"csi/locale",
	"csi/reflection",
	"csi/store/_base",
	"csi/store/Names",
	"csi/widget/FilteringSelect",
	"csi/xml",
	"csi/xml/Search",
	"dojo/text!./TaskActionGroup.html",
	
	"csi/widget/ValidationTextBox"
], function(declare,_TemplatedMixin,RadioButton,TitlePane,_WidgetsInTemplateMixin,_WidgetBase,_,csi,Deferred,html,locale,
			reflection,csiStore,Names,FilteringSelect,xml,Search,template) {

	const ATTR_COMMENT_REQUIRED = "CommentsRequired";
	const ATTR_VALIDATION_GROUP = "ValidationGroup";
	const VALUE_OPTIONAL = "optional";
	const VALUE_REQUIRED = "required";
	
	return declare([_WidgetBase,_TemplatedMixin,_WidgetsInTemplateMixin], {
		baseClass: "csi-task-action-group",
		templateString: template,
		
		/** @property {boolean} bSelectionChanged - set to true to indicate the selection is changed and selectionSetting on the node should not be used anymore 
		 * However, if user click cancel and then re-enter editing - this data will become actual again */
		bSelectionChanged: false,
		
		/** @property {Object} actionSettings - Object to store current Actions settings */
		actionSettings: null,

		/** @property {_WidgetBase} formValidationNode - child widget that handles validation for overall widget */
		formValidationNode: null,
		
		/** @property {dMemory} store - data store for grid (dstore/Memory) */
		store: null,

		/** @property {Store} validationGroupStore - data store for validation group */
		validationGroupStore: null,

		/** @property {Object} view - reference to the form view */
		view: null,
		
		/**
		 * @override
		 * Apply validation to nested textbox
		 */
		postCreate: function() {
			this.inherited(arguments);
			this.formValidationNode = this.textbox;
		},
		
		/** 
		 * Return view's CamundaTaskDefinition node
		 * @return {Node} 
		 */
		getTaskDefinitionNode: function(){
			var view = this.view;
			return view.getContext().getNode();
		},
		
		/**
		 * Validate widget
		 * @return {boolean}
		 */
		validate: function() {
			return this.textbox.validate();
		},
		
		/**
		 * Forward required flag to textbox
		 * @param {boolean} value
		 */
		_setRequiredAttr: function(value) {
			this._set(VALUE_REQUIRED, value);
			this.textbox.set(VALUE_REQUIRED, value);
		},
		
		/**
		 * Pass store onto grid
		 * @param {dMemory} value
		 */
		_setStoreAttr: function(value) {
			this._set("store", value);
			this.textbox.set("store", value);
		},
		
		/**
		 * Pass store onto grid
		 * @param {string} value - url to selected TaskActionGroup
		 */
		_setValueAttr: function(value) {
			if (!_.isEqual(value, this.value)) {
				this._set("value", value);
				this.textbox.set("value", value);
				this._setActions(value);
			}
		},
		
		/** 
		 * Remove Task's ActionSetting reference 
		 */
		resetTaskActionSettings: function() {
			this.bSelectionChanged = true;
			this.resetActionSettings();
		},
		
		/** Reset our current actions configuration
		 *  @return {Object} - reference to this.actionSettings 
		 */
		resetActionSettings: function(){
			return this.actionSettings = {};
		},

		/**
		 * @param {string} value - url to selected TaskActionGroup
		 */
		_setActions: function(value){
			var self = this;
			var actionSettings = this.resetActionSettings();
			var cache = this.view.getCache();
			var dfd = cache.getNodeDependencies([value], ["Child:Actions"], true);
			
			dfd.then(function(){
				html.clear(self.actionsNode, true);

				var node = cache.getNode(value);
				if (!node){
					return;
				}

				if (!self.bSelectionChanged){
					_.each(self.getTaskDefinitionNode().ActionSettings, function(url, actionName) {
						var settingsNode = cache.getNode(url);
						var settings = actionSettings[actionName] = {};
						settings[ATTR_COMMENT_REQUIRED] = settingsNode.CommentsRequired;
						settings[ATTR_VALIDATION_GROUP] = settingsNode.ValidationGroup;
					});
				}
				
				const actions = csi.sort(cache.getNodes(node.Actions), "SortOrder");
				
				_.each(actions, function(action, i) {
					// only expand the first Title Pane
					self.createActionNode(action, !i);
				});
			});
		},
		
		/**
		 * Create collapsable Action TitlePane with edit controls
		 * @param {Node} action
		 * @param {boolean} expand - indicate whether expand the pane or not
		 */
		createActionNode: function(action, expand) {
			if (!action){
				return;
			}
			
			var self = this;
			var actionName = action.$Name;
			var settings = this.actionSettings;
			var settingObj = settings[actionName] = settings[actionName] || {};
			
			var tp = new TitlePane({
				title: locale.getNodeStoredName(action),
				tooltip: "",
				'class':'csi-camunda-action',
				open: expand
			});
			var domNode = tp.containerNode;
			
			// "Comments" Label
			html.create('div',{'class':'csi-camunda-action-setting bold', 'innerText': locale.resource("CamundaActionComments")}, domNode );
			
			// "Comments Optional" radio button with label
			var uniqName = _.uniqueId(ATTR_COMMENT_REQUIRED); 
			var required = settingObj[ATTR_COMMENT_REQUIRED];
			var btn1 = new RadioButton({
				name: uniqName,
				value: 	VALUE_OPTIONAL,
				checked: !required,
				disabled: false,
				onClick: function(value){
					self.actionSettings[actionName][ATTR_COMMENT_REQUIRED] = false;
				}
			}).placeAt(domNode);
			html.create('label',{'for': btn1.id, 'innerText': locale.resource("CamundaActionCommentsOptional")}, domNode );
			
			// "Comments Required" radio button with label
			var btn2 = new RadioButton({
				name: uniqName,
				value: VALUE_REQUIRED,
				checked: required,
				disabled: false,
				onClick: function(value){
					self.actionSettings[actionName][ATTR_COMMENT_REQUIRED] = true;
				}
			}).placeAt(domNode);
			html.create('label',{'for': btn2.id, 'innerText': locale.resource("CamundaActionCommentsRequired")}, domNode );
			
			// Validation group label and select
			html.create('div',{'class':'csi-camunda-action-setting bold', 'innerText': locale.typeId("CamundaTaskDefinitionActionSettings", "ValidationGroup")}, domNode );
			var primaryType = this.view.primaryType;
			var rc = reflection.getClass(primaryType);
			var boUrl = rc.getBusinessObject();
			var search = new Search();
			search.byType("ConfigurableValidationRuleGroup");
			search.setLimitPath(boUrl, "Child:ValidationRuleGroups");
			search.getQuery().Attr("Node Name", xml.OP_NOT_EQUALS, "");
			var store = this.validationGroupStore || new Names({
				data: [csiStore.nameObject("")],
				request: search.getRequest(),
				idProperty: "Name"
			});

			this.validationGroupStore = store;
			new FilteringSelect({
				store: store,
				value: settingObj[ATTR_VALIDATION_GROUP],
				onChange: function(val) {
					self.actionSettings[actionName][ATTR_VALIDATION_GROUP] = val;
				}
			}).placeAt(domNode);

			tp.placeAt(this.actionsNode);
		},
		
		/**
		 * @override
		 * Do nothing on control focus
		 */
		focus: function() { },
		
		/**
		 * Set new widget value; if selection changed, then remove old action data
		 * @param {string} value - url of selected ActionGroup
		 */
		_onChangeTextBox: function(value) {
			if (!_.isEqual(value, this.value)) {
				this.resetTaskActionSettings();
				this.set("value", value);
			}
		}
	});
});